import { useState } from 'react';

import { supabase } from '../config/supabase';

import { LogoFull } from './logo';

/**
 * Owner sign-in screen. Email + password against the same Supabase
 * project the user-facing app uses — the owner's auth.users row is
 * already there. After successful sign-in, the global auth-state
 * subscription pushes the new session into the Zustand store and
 * <OwnerGate> re-renders with the dashboard (or lockout if the
 * email is not in OWNER_EMAILS).
 *
 * Magic-link / OAuth sign-in are deliberately not offered here — for
 * a single-owner admin tool, a password is faster and avoids letting
 * an attacker who hijacks an email account into the dashboard.
 */
export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setSubmitting(false);
    if (signInError) setError(signInError.message);
  };

  return (
    <div className="nw-lockout">
      <LogoFull size={48} tagline="Admin" />
      <h1 className="nw-lockout__title">Sign in</h1>
      <p className="nw-lockout__body">
        Owner-only console. Sign in with the same account you use for the
        NewsWave app.
      </p>
      <form
        onSubmit={onSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-md)',
          width: '100%',
          maxWidth: 360,
        }}
      >
        <input
          type="email"
          required
          autoComplete="username"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
        <input
          type="password"
          required
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
        {error && (
          <p
            style={{
              color: 'var(--error)',
              fontSize: 'var(--fs-sm)',
              margin: 0,
            }}
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          className="nw-btn nw-btn--primary"
          disabled={submitting}
          style={{ justifyContent: 'center' }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--fs-md)',
  padding: '10px 14px',
  borderRadius: 'var(--r-md)',
  border: '0.5px solid var(--card-border)',
  background: 'var(--card)',
  color: 'var(--text-primary)',
  outline: 'none',
};
